import React, { useContext, useEffect, useRef, useState } from "react";
import MyContext from "../../context/MyContext";
import AuthContext from "../../context/userAuth.context";
import { voiceTrim, secondsToTime } from "../../Utils/soundTrim";

// "Voice" in a character's settings: play only part of its speech (Text to Speech or recorded
// voice). TTSTrimStart / TTSTrimEnd are seconds of the voice file. The voice block on the timeline,
// the mouth movement, the preview and the export all use the trimmed part.

const VoiceTrimSettings = ({ layerKey }) => {
  const { LayersData, setLayersData, ChangeCount, setChangeCount, calculateWidthByDuration, UndoLayer, setUndoLayer, setRedoLayer } =
    useContext(MyContext);
  const { authenticationData } = useContext(AuthContext);
  const layer = (LayersData || []).find((l) => l.key === layerKey);
  const [duration, setDuration] = useState(0);
  const player = useRef(null);
  const stopAt = useRef(null);

  const path = layer && layer.TTSPath;
  const known = layer ? voiceTrim(layer).full : 0;

  // The voice's real length (TTSDuration can be missing on older projects).
  useEffect(() => {
    setDuration(known);
    if (!path || known > 0) return undefined;
    const a = new Audio(authenticationData.ApiBaseURL + "/" + path);
    const done = () => Number.isFinite(a.duration) && setDuration(a.duration);
    a.addEventListener("loadedmetadata", done);
    return () => a.removeEventListener("loadedmetadata", done);
  }, [path, known, authenticationData.ApiBaseURL]);

  useEffect(
    () => () => {
      if (player.current) player.current.pause();
      clearInterval(stopAt.current);
    },
    []
  );

  if (!layer || !layer.TTSName || !path) return null;
  const full = duration || known;
  if (!(full > 0)) return null;
  const trim = voiceTrim({ ...layer, TTSDuration: layer.TTSDuration || full });
  const trimmed = trim.start > 0.001 || trim.end < full - 0.001;

  const toPx = (sec) => {
    try {
      const px = calculateWidthByDuration(secondsToTime(sec));
      if (Number.isFinite(px)) return px;
    } catch (e) {
      /* fall back below */
    }
    const onePx = Number(localStorage.getItem("onePxEquivalent")) || 8.620689655172415;
    return Math.round((sec * 1000) / onePx);
  };

  const save = (start, end) => {
    const s = Math.max(0, Math.min(start, full - 0.1));
    const e = Math.max(s + 0.1, Math.min(end, full));
    const stack = [...(UndoLayer || [])];
    stack.push([...LayersData]);
    if (stack.length > 10) stack.shift();
    if (setUndoLayer) setUndoLayer(stack);
    if (setRedoLayer) setRedoLayer([]);
    const px = toPx(e - s);
    setLayersData(
      LayersData.map((l) => {
        if (l.key !== layerKey) return l;
        const isFull = s <= 0.001 && e >= full - 0.001;
        const next = { ...l };
        if (isFull) {
          delete next.TTSTrimStart;
          delete next.TTSTrimEnd;
        } else {
          next.TTSTrimStart = Math.round(s * 100) / 100;
          next.TTSTrimEnd = Math.round(e * 100) / 100;
        }
        // The voice block on the timeline (and the mouth movement) follow the trimmed length.
        next.midTransitionWidths = px;
        next.TTSWidthOnTimeline = px;
        next.completeWithOfTTS = px;
        if (!next.TTSDuration) next.TTSDuration = secondsToTime(full);
        return next;
      })
    );
    if (setChangeCount) setChangeCount((ChangeCount || 0) + 1);
  };

  const listen = () => {
    if (player.current) player.current.pause();
    clearInterval(stopAt.current);
    const a = new Audio(authenticationData.ApiBaseURL + "/" + path);
    player.current = a;
    a.currentTime = trim.start;
    const p = a.play();
    if (p && p.catch) p.catch(() => {});
    stopAt.current = setInterval(() => {
      if (a.paused || a.currentTime >= trim.end) {
        a.pause();
        clearInterval(stopAt.current);
      }
    }, 30);
  };

  const row = { display: "flex", justifyContent: "space-between", fontSize: 12, marginTop: 6 };

  return (
    <div className="LabelWithSlider" style={{ marginTop: 12, marginBottom: 12 }}>
      <div className="settingsTitle mb-2">Voice: {layer.TTSName}</div>
      <div style={row}>
        <span>Start at</span>
        <b>{trim.start.toFixed(1)} s</b>
      </div>
      <input
        type="range"
        min="0"
        max={full.toFixed(2)}
        step="0.1"
        value={trim.start}
        onChange={(ev) => save(Number(ev.target.value), trim.end)}
        style={{ width: "100%" }}
        aria-label="Voice starts at"
      />
      <div style={row}>
        <span>End at</span>
        <b>{trim.end.toFixed(1)} s</b>
      </div>
      <input
        type="range"
        min="0"
        max={full.toFixed(2)}
        step="0.1"
        value={trim.end}
        onChange={(ev) => save(trim.start, Number(ev.target.value))}
        style={{ width: "100%" }}
        aria-label="Voice ends at"
      />
      <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>
        Plays {trim.length.toFixed(1)} s of {full.toFixed(1)} s.
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <button type="button" onClick={listen} style={{ flex: 1, padding: "4px 8px", borderRadius: 4, border: "1px solid #c9d3e6", background: "#fff", cursor: "pointer" }}>
          ▶ Listen
        </button>
        <button
          type="button"
          onClick={() => save(0, full)}
          disabled={!trimmed}
          style={{ flex: 1, padding: "4px 8px", borderRadius: 4, border: "1px solid #c9d3e6", background: "#fff", cursor: trimmed ? "pointer" : "default", opacity: trimmed ? 1 : 0.5 }}
        >
          Use whole voice
        </button>
      </div>
    </div>
  );
};

export default VoiceTrimSettings;
